/**
 * SSR Error Context (client-safe)
 *
 * Provides the SSR error handler context and hook used by the Slot component.
 * This file does NOT import react-dom/server and is safe to include in
 * client bundles. The actual SSR rendering logic lives in ssr.ts which
 * is only imported by the SSG entry point (Node-only).
 */

import React, { createContext, useContext, type ReactNode } from 'react';

export type SSRErrorHandler = (slot: string, error: unknown) => void;

const SSRErrorContext = createContext<SSRErrorHandler | null>(null);

/**
 * Hook for Slot to check if SSR error handling is active.
 * When an error handler is present, Slot wraps its render in try/catch.
 */
export function useSSRErrorHandler(): SSRErrorHandler | null {
  return useContext(SSRErrorContext);
}

export interface SSRErrorProviderProps {
  onError: SSRErrorHandler;
  children: ReactNode;
}

/**
 * Wraps the component tree with an SSR error handler.
 */
export function SSRErrorProvider({ onError, children }: SSRErrorProviderProps) {
  return React.createElement(SSRErrorContext.Provider, { value: onError }, children);
}
